import type { MessagingConversation } from '../../../api/generated/model/messagingConversation'
import { MessagingConversationStatus } from '../../../api/generated/model/messagingConversationStatus'
import { MessagingAuthorKind } from '../../../api/generated/model/messagingAuthorKind'
import type { MessagingMessage } from '../../../api/generated/model/messagingMessage'
import { MessagingMessageKind } from '../../../api/generated/model/messagingMessageKind'
import { MessagingSide } from '../../../api/generated/model/messagingSide'
import { MessagingTopic } from '../../../api/generated/model/messagingTopic'
import type { MessagingSurface } from './MessageBubble'

type ProjectionSafetyReason =
  | 'wrong_side'
  | 'entity_type'
  | 'route'
  | 'unknown_field'
  | 'malformed'

export type ProjectionSafetyResult<T> =
  | { safe: true; value: T }
  | { safe: false; reason: ProjectionSafetyReason }

const CONVERSATION_KEYS = new Set([
  'id',
  'side',
  'topic',
  'status',
  'entity_link',
  'subject',
  'unread_count',
  'last_message_preview',
  'created_at',
  'updated_at',
])

const ENTITY_LINK_KEYS = new Set(['entity_type', 'entity_id', 'label', 'route'])

const MESSAGE_KEYS = new Set([
  'id',
  'conversation_id',
  'client_message_id',
  'kind',
  'body',
  'author_kind',
  'attachment_ids',
  'reply_to_message_id',
  'created_at',
  'edited_at',
  'retracted_at',
])

const SUPPLY_ENTITY_TYPES = new Set([
  'work_order',
  'purchase_order',
  'sample',
])
const ENTITY_TYPES = new Set(['order', 'organization', ...SUPPLY_ENTITY_TYPES])
const CONVERSATION_SIDES = new Set<string>(Object.values(MessagingSide))
const CONVERSATION_TOPICS = new Set<string>(Object.values(MessagingTopic))
const CONVERSATION_STATUSES = new Set<string>(
  Object.values(MessagingConversationStatus),
)
const MESSAGE_KINDS = new Set<string>(Object.values(MessagingMessageKind))
const MESSAGE_AUTHORS = new Set<string>(Object.values(MessagingAuthorKind))

export type ValidatedEntityRoute =
  | { kind: 'seller_order'; id: string }
  | { kind: 'supply_work_order'; id: string }
  | { kind: 'supply_sample'; id: string }
  | { kind: 'supply_purchase_order' }

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function hasOnlyKeys(value: Record<string, unknown>, allowed: Set<string>) {
  return Object.keys(value).every((key) => allowed.has(key))
}

function isOptionalNullableString(value: unknown): boolean {
  return value === undefined || value === null || typeof value === 'string'
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  )
}

function isInternalRoute(value: unknown): value is string {
  return (
    typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
  )
}

export function validateEntityRoute(
  conversation: MessagingConversation,
  surface: MessagingSurface,
): ValidatedEntityRoute | null {
  const entity = conversation.entity_link
  const encodedId = encodeURIComponent(entity.entity_id)

  if (
    surface === 'seller' &&
    entity.entity_type === 'order' &&
    entity.route === `/seller/my-orders/${encodedId}`
  ) {
    return { kind: 'seller_order', id: entity.entity_id }
  }
  if (surface !== 'supply') return null

  if (
    entity.entity_type === 'work_order' &&
    entity.route === `/supply/work-orders/${encodedId}`
  ) {
    return { kind: 'supply_work_order', id: entity.entity_id }
  }
  if (
    entity.entity_type === 'sample' &&
    entity.route === `/supply/samples/${encodedId}`
  ) {
    return { kind: 'supply_sample', id: entity.entity_id }
  }
  if (
    entity.entity_type === 'purchase_order' &&
    entity.route === '/supply/po-inbox'
  ) {
    return { kind: 'supply_purchase_order' }
  }
  return null
}

export function validateConversationProjection(
  value: unknown,
  surface: MessagingSurface,
): ProjectionSafetyResult<MessagingConversation> {
  if (!isRecord(value)) return { safe: false, reason: 'malformed' }
  if (!hasOnlyKeys(value, CONVERSATION_KEYS)) {
    return { safe: false, reason: 'unknown_field' }
  }

  const entity = value.entity_link
  if (!isRecord(entity)) return { safe: false, reason: 'malformed' }
  if (!hasOnlyKeys(entity, ENTITY_LINK_KEYS)) {
    return { safe: false, reason: 'unknown_field' }
  }
  if (
    typeof value.id !== 'string' ||
    typeof value.side !== 'string' ||
    !CONVERSATION_SIDES.has(value.side) ||
    typeof value.topic !== 'string' ||
    !CONVERSATION_TOPICS.has(value.topic) ||
    typeof value.status !== 'string' ||
    !CONVERSATION_STATUSES.has(value.status) ||
    !isOptionalNullableString(value.subject) ||
    typeof value.unread_count !== 'number' ||
    !Number.isInteger(value.unread_count) ||
    value.unread_count < 0 ||
    !isOptionalNullableString(value.last_message_preview) ||
    typeof value.created_at !== 'string' ||
    typeof value.updated_at !== 'string' ||
    typeof entity.entity_type !== 'string' ||
    !ENTITY_TYPES.has(entity.entity_type) ||
    typeof entity.entity_id !== 'string' ||
    entity.entity_id.length === 0 ||
    typeof entity.label !== 'string' ||
    entity.label.length === 0 ||
    (entity.route !== null && typeof entity.route !== 'string')
  ) {
    return { safe: false, reason: 'malformed' }
  }

  const conversation = value as unknown as MessagingConversation
  if (surface !== 'admin' && value.side !== surface) {
    return { safe: false, reason: 'wrong_side' }
  }

  if (entity.entity_type === 'organization') {
    if (entity.route !== null) return { safe: false, reason: 'route' }
    if (entity.label !== 'PEXIFY Support' || !isUuid(entity.entity_id)) {
      return { safe: false, reason: 'malformed' }
    }
    return { safe: true, value: conversation }
  }

  if (!isInternalRoute(entity.route)) {
    return { safe: false, reason: 'route' }
  }

  if (surface === 'admin') return { safe: true, value: conversation }

  if (surface === 'seller') {
    if (entity.entity_type !== 'order') {
      return { safe: false, reason: 'entity_type' }
    }
  } else {
    if (!SUPPLY_ENTITY_TYPES.has(entity.entity_type)) {
      return { safe: false, reason: 'entity_type' }
    }
  }

  if (!validateEntityRoute(conversation, surface)) {
    return { safe: false, reason: 'route' }
  }

  return { safe: true, value: conversation }
}

export function validateMessageProjection(
  value: unknown,
  conversationId: string,
): ProjectionSafetyResult<MessagingMessage> {
  if (!isRecord(value)) return { safe: false, reason: 'malformed' }
  if (!hasOnlyKeys(value, MESSAGE_KEYS)) {
    return { safe: false, reason: 'unknown_field' }
  }
  if (
    typeof value.id !== 'string' ||
    value.conversation_id !== conversationId ||
    !isOptionalNullableString(value.client_message_id) ||
    typeof value.kind !== 'string' ||
    !MESSAGE_KINDS.has(value.kind) ||
    typeof value.body !== 'string' ||
    typeof value.author_kind !== 'string' ||
    !MESSAGE_AUTHORS.has(value.author_kind) ||
    !Array.isArray(value.attachment_ids) ||
    !value.attachment_ids.every((id) => typeof id === 'string') ||
    !isOptionalNullableString(value.reply_to_message_id) ||
    typeof value.created_at !== 'string' ||
    !isOptionalNullableString(value.edited_at) ||
    !isOptionalNullableString(value.retracted_at)
  ) {
    return { safe: false, reason: 'malformed' }
  }

  return { safe: true, value: value as unknown as MessagingMessage }
}
